import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BookOpen, Heart, Brain, Target, Infinity, ListChecks, ImageOff } from "lucide-react";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } }),
};

const partIcons = [Heart, ListChecks, BookOpen, Brain, Infinity];

export default function JournalPage() {
  const [journal, setJournal] = useState(null);
  const [journalImage, setJournalImage] = useState("");

  useEffect(() => {
    axios.get(`${API}/journal`).then((r) => setJournal(r.data)).catch(console.error);
    axios.get(`${API}/site-settings`).then((r) => { if (r.data.journal_image) setJournalImage(r.data.journal_image); }).catch(console.error);
  }, []);

  if (!journal) {
    return (
      <div className="min-h-screen bg-champagne" data-testid="journal-page">
        <Navbar />
        <div className="pt-32 text-center">
          <p className="font-body text-charcoal-light">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-champagne" data-testid="journal-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6" data-testid="journal-hero">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-5"
            >
              <div className="rounded-[2rem] overflow-hidden shadow-lift bg-champagne-dark aspect-[3/4] flex items-center justify-center">
                {(journal.images?.[0] || journalImage) ? (
                  <img src={journal.images?.[0] || journalImage} alt={journal.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center p-8">
                    <ImageOff className="w-12 h-12 text-soft-grey mx-auto" />
                  </div>
                )}
              </div>
            </motion.div>

            <div className="lg:col-span-7 space-y-6">
              <motion.h1 initial="hidden" animate="visible" variants={fadeUp} custom={0}
                className="font-heading text-4xl md:text-5xl font-semibold text-charcoal leading-tight"
              >
                A Structured Journal for Thinking & Growth
              </motion.h1>
              <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={1}
                className="font-body text-charcoal-light leading-relaxed text-lg space-y-4"
              >
                <p>This is not just a place to write.</p>
                <p>It is a simple, structured system designed to help you understand your thoughts, emotions, and behaviours through daily reflection.</p>
                <p>Using practical frameworks and guided sections, it supports clearer thinking, greater awareness, and more intentional responses in everyday life.</p>
                <p>Designed for children, teens, and adults, it can be used independently or alongside guidance.</p>
              </motion.div>
              <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={2} className="flex flex-wrap gap-4">
                <span
                  data-testid="journal-order-btn"
                  className="inline-flex items-center gap-2 bg-muted text-charcoal-light px-8 py-4 rounded-full font-body font-semibold text-lg cursor-default"
                >
                  Order Journal — Coming Soon
                </span>
                <span
                  data-testid="journal-video-btn"
                  className="inline-flex items-center gap-2 border-2 border-border text-charcoal-light px-8 py-4 rounded-full font-body font-semibold text-lg cursor-default"
                >
                  Watch How It Works — Coming Soon
                </span>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Five Key Parts */}
      <section className="py-20 bg-white" data-testid="journal-features">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-charcoal">
              Structured into five key parts
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {journal.features.map((f, i) => {
              const Icon = partIcons[i % partIcons.length];
              const [title, ...descParts] = f.split(" \u2014 ");
              const desc = descParts.join(" — ");
              return (
                <motion.div
                  key={i}
                  initial="hidden" whileInView="visible" viewport={{ once: true }}
                  variants={fadeUp} custom={i * 0.5}
                  className="bg-off-white border border-border rounded-2xl p-6 hover:shadow-soft transition-all"
                >
                  <div className="w-10 h-10 bg-navy/10 rounded-xl flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-navy" />
                  </div>
                  {desc ? (
                    <>
                      <p className="font-heading text-lg font-semibold text-charcoal mb-2">{title}</p>
                      <p className="font-body text-sm text-charcoal-light leading-relaxed">{desc}</p>
                    </>
                  ) : (
                    <p className="font-body text-sm text-charcoal-light leading-relaxed">{f}</p>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Supporting info */}
      <section className="py-16 px-6" data-testid="journal-info">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-body text-charcoal-light leading-relaxed text-lg mb-4">
            This journal supports the development of self-awareness, emotional clarity, and more intentional responses in daily life.
          </p>
          <p className="font-body text-charcoal-light leading-relaxed text-lg">
            It is built on the same frameworks used in Joyce's sessions, and is part of her own daily practice as well as the work she does with clients across different stages of life.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
